import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FulfillmentOrder } from '@vendasta/order-fulfillment';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import { Order, Order as SalesOrder, SalesOrdersService } from '@vendasta/sales-orders';
import { User } from '@vendasta/store';
import {
  WorkOrderFormComponent,
  WorkOrderFormService,
  WorkOrderPersona,
  WorkOrderUnsavedChangesComponent,
  WorkOrderUnsavedChangesDialogComponent,
  WorkOrderUnsavedChangesDialogResult,
  clearUnsavedChangesFromSessionStorage,
  doesWorkOrderFormHaveUnsavedChanges,
  getTemporaryStorageKey,
} from '@vendasta/work-order';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { OrderSubmittedService } from './order-submitted.service';

@Component({
  selector: 'orders-submitted',
  templateUrl: './order-submitted.component.html',
  styleUrls: ['./order-submitted.component.scss'],
})
export class OrderSubmittedComponent
  extends WorkOrderUnsavedChangesComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() users: User[] = [];
  @Input() order: Order;
  @Input() persona: WorkOrderPersona = WorkOrderPersona.CP;
  @Input() salesOrderUrl: string;
  @Input() accountDetailsUrl: string;
  @Input() autoAdvance = false;

  workOrderFormInfos$: Observable<FulfillmentOrder[]>;
  showFulfillmentForm$: Observable<boolean>;
  pageLoading$: Observable<boolean>;
  private woFormDoneLoadingMap$$: BehaviorSubject<Map<number, boolean>> = new BehaviorSubject(
    new Map<number, boolean>(),
  );
  woFormDoneLoading$: Observable<boolean>;

  @ViewChildren(WorkOrderFormComponent) woFormComponents: QueryList<WorkOrderFormComponent>;
  private combinedFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  private subscriptions: Subscription[] = [];

  private numberItemsUsingFulfillmentForms$$: ReplaySubject<number> = new ReplaySubject(1);
  private someProductHasFulfillmentFormLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private foFormStatusState: Map<string, boolean> = new Map<string, boolean>();

  constructor(
    private orderSubmittedService: OrderSubmittedService,
    private workOrderFormService: WorkOrderFormService,
    private salesOrderService: SalesOrdersService,
    private analytics: ProductAnalyticsService,
    private readonly router: Router,
    private dialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    const appIds = this.orderSubmittedService.salesOrder$.pipe(
      filterNullAndUndefined(),
      distinctUntilChanged(),
      tap(() => this.someProductHasFulfillmentFormLoading$$.next(true)),
      switchMap((salesOrder) =>
        this.salesOrderService.previewOrderActivations(salesOrder.orderId, salesOrder.businessId),
      ),
      map((activations) => activations.map((activation) => activation.productId)),
      catchError(() => {
        return of([]);
      }),
    );

    this.showFulfillmentForm$ = appIds.pipe(
      switchMap((appIds) =>
        this.orderSubmittedService
          .getTotalItemsThatHaveFulfillmentForms(appIds)
          .pipe(tap((total) => this.numberItemsUsingFulfillmentForms$$.next(total))),
      ),
      map((total) => total > 0),
      tap(() => this.someProductHasFulfillmentFormLoading$$.next(false)),
      finalize(() => this.someProductHasFulfillmentFormLoading$$.next(false)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.showFulfillmentForm$.pipe(
      map((someProductHasFulfillmentForm) => {
        if (someProductHasFulfillmentForm) {
          this.trackOrderHasAppsWithFulfillmentForms(this.order);
        }
        if (this.autoAdvance) {
          this.router.navigate([this.salesOrderUrl]);
        }
      }),
    );

    this.showFulfillmentForm$
      .pipe(
        map((someProductHasFulfillmentForm) => {
          if (!someProductHasFulfillmentForm && this.autoAdvance) {
            this.router.navigate([this.salesOrderUrl]);
          }
        }),
      )
      .subscribe();

    this.workOrderFormInfos$ = this.orderSubmittedService.salesOrder$.pipe(
      filterNullAndUndefined(),
      distinctUntilChanged(),
      switchMap((salesOrder) =>
        this.workOrderFormService.getOrdersToRenderFormsFor(salesOrder.orderId, salesOrder.businessId),
      ),
      tap((woFormInfos) => {
        woFormInfos.forEach((_, i) => {
          const loadingMap: Map<number, boolean> = this.woFormDoneLoadingMap$$.getValue();
          loadingMap.set(i, false);
          this.woFormDoneLoadingMap$$.next(loadingMap);
        });
      }),
    );

    this.pageLoading$ = combineLatest([this.workOrderFormInfos$, this.someProductHasFulfillmentFormLoading$$]).pipe(
      map(([, someProductHasFulfillmentFormLoading]) => someProductHasFulfillmentFormLoading),
    );

    this.woFormDoneLoading$ = this.woFormDoneLoadingMap$$.pipe(
      map((woLoadingMap) => {
        const loadingValues: boolean[] = Array.from(woLoadingMap.values());
        return loadingValues?.length > 0 && loadingValues.every((value) => value);
      }),
    );
  }

  ngOnChanges(): void {
    this.orderSubmittedService.salesOrder = this.order;
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.woFormComponents.changes.subscribe((workOrderForms) => {
        workOrderForms.forEach((woFormComp) => {
          const key: string = getTemporaryStorageKey(woFormComp.accountGroupId, woFormComp.appId);
          if (!this.combinedFormGroup.contains(key)) {
            this.combinedFormGroup.setControl(key, woFormComp.form);
          }
          // create item in our state map initially, for emitting posthog event
          this.foFormStatusState.set(woFormComp.appId, false);
        });
      }),
    );
  }

  canDeactivate(): boolean {
    return !doesWorkOrderFormHaveUnsavedChanges(this.combinedFormGroup);
  }

  pendingChangesDialog(): boolean | Observable<boolean> {
    this.dialog.closeAll();
    return this.dialog
      .open(WorkOrderUnsavedChangesDialogComponent)
      .afterClosed()
      .pipe(
        map((result: WorkOrderUnsavedChangesDialogResult) => {
          return result === WorkOrderUnsavedChangesDialogResult.CONTINUE_EDITING ? false : !!result;
        }),
        tap((result: boolean) => {
          if (result) {
            clearUnsavedChangesFromSessionStorage(this.combinedFormGroup);
          }
        }),
      );
  }

  /**
   * Tracks whether the order has a product in it that contains a fulfillment form
   * @param order: The Sales Order
   */
  private trackOrderHasAppsWithFulfillmentForms(order: SalesOrder): void {
    const appIds: string[] = order?.lineItems?.map((li) => li?.appKey?.appId);
    this.orderSubmittedService
      .someItemsHaveFulfillmentForms(appIds)
      .pipe(take(1))
      .subscribe((haveFulfillmentForms) => {
        if (haveFulfillmentForms) {
          this.analytics.trackEvent(
            'order-placed-with-fulfillment-form',
            'work-orders',
            'order-placed-with-fulfillment-form',
          );
        }
      });
  }

  handleFOFormSubmitClicked(appId: string): void {
    this.foFormStatusState.set(appId, true);
    for (const value of Object.values(this.foFormStatusState)) {
      if (!value) {
        return;
      }
    }
    // all forms are complete, emit posthog event
    this.analytics.trackEvent('instant-form-all-submitted', 'work-orders', 'instant-form-all-submitted');
  }

  handleWOFormDoneLoading(index: number): void {
    const loadingMap: Map<number, boolean> = this.woFormDoneLoadingMap$$.getValue();
    loadingMap.set(index, true);
    this.woFormDoneLoadingMap$$.next(loadingMap);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
